import React, { useState, useEffect } from 'react';
import {
  Container,
  Grid,
  Paper,
  Stack,
  Text,
  ActionIcon,
  ScrollArea,
  Badge,
  Group,
  Card,
  Title,
  Divider,
  Box,
  Loader,
  Alert,
  ThemeIcon,
  Collapse,
  UnstyledButton,
  Button,
  rem
} from '@mantine/core';
import {
  IconChevronRight,
  IconChevronDown,
  IconChevronLeft,
  IconFolder,
  IconFolderOpen,
  IconTarget,
  IconDatabase,
  IconChartBar,
  IconGauge,
  IconInfoCircle,
  IconAlertCircle,
  IconCode,
  IconCategory,
  IconEdit,
  IconPlayerPlay as IconPlay,
  IconExternalLink,
  IconX,
  IconSettings
} from '@tabler/icons-react';
import useAuthenticatedData from '../../hooks/useAuthenticatedData';
import config from '../../config';

// Import the QueryEditor component
import QueryEditor from './QueryEditor';

const getNodeIcon = (node, isExpanded = false) => {
  const iconProps = { size: 16 };

  switch (node.nodeType) {
    case 'Category':
      return isExpanded ? <IconFolderOpen {...iconProps} /> : <IconFolder {...iconProps} />;
    case 'Feature':
      return <IconSettings {...iconProps} />;
    case 'Scorecard':
      return <IconChartBar {...iconProps} />;
    case 'KPI':
      return <IconGauge {...iconProps} />;
    case 'Query':
      return <IconTarget {...iconProps} />;
    default:
      return <IconCategory {...iconProps} />;
  }
};

const getNodeColor = (nodeType, queryType, hasError = false) => {
  // A query that failed its last validation shows red, so drift is visible in the tree instead of
  // only surfacing when someone opens the page it backs.
  if (hasError) return 'red';

  switch (nodeType) {
    case 'Category':
      return 'blue';
    case 'Feature':
      return 'purple';
    case 'Scorecard':
      return 'green';
    case 'KPI':
      return 'orange';
    case 'Query':
      // Use queryType to determine the color for Query nodes
      switch (queryType) {
        case 'Summary':
          return 'blue';
        case 'Details':
          return 'purple';
        default:
          return 'gray';
      }
    default:
      return 'gray';
  }
};

const TreeNode = ({ node, level = 0, onNodeSelect, selectedNodeId, expandedNodes, setExpandedNodes }) => {
  const hasChildren = node.children && node.children.length > 0;
  const isExpanded = expandedNodes.has(node.id);
  const isSelected = selectedNodeId === node.id;
  const hasError = Boolean(node.hasError);

  const handleToggle = (e) => {
    e.stopPropagation();
    if (hasChildren) {
      const newExpanded = new Set(expandedNodes);
      if (isExpanded) {
        newExpanded.delete(node.id);
      } else {
        newExpanded.add(node.id);
      }
      setExpandedNodes(newExpanded);
    }
  };

  const handleSelect = () => {
    onNodeSelect(node);
  };

  return (
    <Box>
      <UnstyledButton
        onClick={handleSelect}
        style={{
          display: 'block',
          width: '100%',
          padding: `${rem(8)} ${rem(12)}`,
          borderRadius: rem(4),
          backgroundColor: isSelected ? (hasError ? 'var(--mantine-color-blue-light)' : 'var(--mantine-color-blue-light)') : 'transparent',
          border: isSelected ? `1px solid var(--mantine-color-${hasError ? 'blue' : 'blue'}-filled)` : '1px solid transparent',
          marginLeft: level,
          opacity: hasError ? 0.8 : 1,
        }}
        data-selected={isSelected}
      >
        <Group gap="xs" wrap="nowrap">
          {hasError && (
            <ThemeIcon size="xs" variant="filled" color="red">
              <IconX size={10} />
            </ThemeIcon>
          )}

          {hasChildren && (
            <ActionIcon
              size="xs"
              variant="subtle"
              onClick={handleToggle}
              style={{ minWidth: rem(16) }}
            >
              {isExpanded ? <IconChevronDown size={12} /> : <IconChevronRight size={12} />}
            </ActionIcon>
          )}

          {!hasChildren && <Box style={{ width: rem(16) }} />}

          <ThemeIcon
            size="sm"

            color={getNodeColor(node.nodeType, node.queryType, hasError)}
          >
            {getNodeIcon(node, isExpanded)}
          </ThemeIcon>

          <Text size="sm" style={{ flex: 1 }}>
            {node.name}
          </Text>

          {/*}
          <Badge
            size="xs"
            variant="light"
            color={getNodeColor(node.nodeType, hasError)}
          >
            {node.nodeType}
          </Badge>
            */}

         
        </Group>
      </UnstyledButton>

      {hasChildren && (
        <Collapse in={isExpanded}>
          <Box pl="md">
            {node.children.map((child) => (
              <TreeNode
                key={child.id}
                node={child}
                level={level + 1}
                onNodeSelect={onNodeSelect}
                selectedNodeId={selectedNodeId}
                expandedNodes={expandedNodes}
                setExpandedNodes={setExpandedNodes}
              />
            ))}
          </Box>
        </Collapse>
      )}
    </Box>
  );
};

const QueryDetails = ({ node, onEditQuery, onEditQueryNewWindow, onEditFeature }) => {
  if (!node) {
    return (
      <Card>
        <Group gap="xs">
          <IconInfoCircle size={16} />
          <Text    >Select a node from the tree to view details</Text>
        </Group>
      </Card>
    );
  }

  const isQuery = node.nodeType === 'Query' && node.isTerminal;
  const isFeature = node.nodeType === 'Feature';

  return (
    <Card>
      <Stack gap="md">
        <Group gap="xs">
          <ThemeIcon
            size="lg"
            variant="light"
            color={getNodeColor(node.nodeType, node.queryType)}
          >
            {getNodeIcon(node)}
          </ThemeIcon>
          <Box style={{ flex: 1 }}>
            <Title order={3}>{node.name}</Title>
            <Group gap="xs">
              <Badge color={getNodeColor(node.nodeType, node.queryType)}>
                {node.nodeType}
              </Badge>
              {node.isTerminal && (
                <Badge color="red" variant="light">
                  Terminal
                </Badge>
              )}
            </Group>
          </Box>
        </Group>

        <Divider />

        <Stack gap="sm">
          <Group gap="xs">
            <Text fw={500} size="sm">Type:</Text>
            <Text size="sm"    >{node.nodeType}</Text>
          </Group>

          <Group gap="xs" align="flex-start">
            <Text fw={500} size="sm">Id:</Text>
            <Text size="sm"    >{node.id || 'Not specified'}</Text>
          </Group>

          <Group gap="xs" align="flex-start">
            <Text fw={500} size="sm">Query Type:</Text>
            <Text size="sm"    >{node.queryType || 'Not specified'}</Text>
          </Group>


          {node.description && (
            <Group gap="xs" align="flex-start">
              <Text fw={500} size="sm">Description:</Text>
              <Text size="sm" style={{ flex: 1 }}>
                {node.description}
              </Text>
            </Group>
          )}

          {node.queryKey && (
            <Group gap="xs" align="flex-start">
              <Text fw={500} size="sm">Query Key:</Text>
              <Text size="sm" ff="monospace" style={{ flex: 1 }}>
                {node.queryKey}
              </Text>
            </Group>
          )}

          {isQuery && (
            <Group gap="xs">
              <Text fw={500} size="sm">Error:</Text>
              <Text size="sm"    >{node.hasError ? 'Yes' : 'No'}</Text>
            </Group>
          )}

          {isQuery && (
            <Group gap="xs" align="flex-start">
              <Text fw={500} size="sm">Error Message:</Text>
              <Text size="sm" style={{ flex: 1 }}>
                {node.errorMessage || 'None'}
              </Text>
            </Group>
          )}
        </Stack>

        {isQuery && (
          <>
            <Divider />
            <Box>
              <Group gap="xs" mb="md">
                <IconDatabase size={16} />
                <Text fw={500} size="sm">Query Information</Text>
              </Group>
              <Text size="sm" mb="md">
                This is an executable query that can be edited and tested.
                Use the buttons below to interact with this query.
              </Text>

              <Group gap="md">
                <Button
                  leftSection={<IconExternalLink size={16} />}
                  variant="light"
                  color="blue"
                  onClick={() => onEditQueryNewWindow(node.queryKey, node.id)}
                >
                  Edit Query
                </Button>

                <Button
                  leftSection={<IconPlay size={16} />}
                  variant="light"
                  color="green"
                  onClick={() => {
                    // Handle quick test - could open a modal or navigate to test view
                    console.log('Quick test for query:', node.queryKey);
                  }}
                >
                  Quick Test
                </Button>
              </Group>
            </Box>
          </>
        )}

        {isFeature && (
          <>
            <Divider />
            <Box>
              <Group gap="xs" mb="md">
                <IconSettings size={16} />
                <Text fw={500} size="sm">Feature Information</Text>
              </Group>
              <Text size="sm" mb="md">
                This is a feature that can be configured and customized.
                Use the button below to edit feature settings.
              </Text>

              <Group gap="md">
                <Button
                  leftSection={<IconEdit size={16} />}
                  variant="light"
                  color="purple"
                  onClick={() => onEditFeature(node.featureKey)}
                >
                  Edit Feature
                </Button>
              </Group>
            </Box>
          </>
        )}

        {!isQuery && !isFeature && node.children && node.children.length > 0 && (
          <>
            <Divider />
            <Box>
              <Group gap="xs" mb="xs">
                <IconFolder size={16} />
                <Text fw={500} size="sm">Container Information</Text>
              </Group>
              <Text size="xs"    >
                This node contains {node.children.length} child {node.children.length === 1 ? 'item' : 'items'}.
                Expand it in the tree to explore the contents.
              </Text>
            </Box>
          </>
        )}
      </Stack>
    </Card>
  );
};

// Component to render QueryEditor in a popup window
const QueryEditorWindow = ({ queryKey, onClose }) => {
  const [isWindowOpen, setIsWindowOpen] = useState(true);

  const handleClose = () => {
    setIsWindowOpen(false);
    onClose();
  };

  if (!isWindowOpen) {
    return null;
  }

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          handleClose();
        }
      }}
    >
      <div
        style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          width: '95vw',
          height: '95vh',
          overflow: 'auto',
          position: 'relative'
        }}
      >
        <Button
          variant="subtle"
          color="red"
          onClick={handleClose}
          style={{
            position: 'absolute',
            top: '10px',
            right: '10px',
            zIndex: 1001
          }}
        >
          ✕ Close
        </Button>
        <QueryEditor queryKey={queryKey} />
      </div>
    </div>
  );
};

const NewQueryCommanderHome = () => {
  const [selectedNode, setSelectedNode] = useState(null);
  const [expandedNodes, setExpandedNodes] = useState(new Set([1, 2, 3])); // Default expanded
  const [currentView, setCurrentView] = useState('explorer'); // 'explorer' or 'editor'
  const [editingQueryKey, setEditingQueryKey] = useState(null);
  const [openWindows, setOpenWindows] = useState(new Set()); // Track open query editor windows

  // Fetch the feature tree data
  const { data: treeData, isLoading: loading, error } = useAuthenticatedData(
    `${config.apiBaseUrl}/queries/feature-tree`
  );

  const handleNodeSelect = (node) => {
    setSelectedNode(node);
  };

  const handleEditQuery = (queryKey) => {
    setEditingQueryKey(queryKey);
    setCurrentView('editor');
  };

  const handleEditQueryNewWindow = (queryKey, featureId) => {
    const url =
      `/query-commander/editor?` +
      `queryKey=${encodeURIComponent(queryKey)}` +
      `&featureId=${encodeURIComponent(featureId)}`;

    // '_blank' will open in a new tab, 'noopener,noreferrer' for security
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleEditFeature = (featureKey) => {
    const url = `/query-commander/feature-editor?featureKey=${encodeURIComponent(featureKey)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleCloseQueryWindow = (queryKey) => {
    setOpenWindows(prev => {
      const newSet = new Set(prev);
      newSet.delete(queryKey);
      return newSet;
    });
  };

  const handleBackToExplorer = () => {
    setCurrentView('explorer');
    setEditingQueryKey(null);
  };

  if (loading) {
    return (
      <Container size="xl" py="md">
        <Paper p="md">
          <Group gap="xs">
            <Loader size="sm" />
            <Text>Loading Query Commander...</Text>
          </Group>
        </Paper>
      </Container>
    );
  }

  if (error) {
    return (
      <Container size="xl" py="md">
        <Alert icon={<IconAlertCircle size={16} />} color="red">
          Error loading Query Commander: {error.message || String(error)}
        </Alert>
      </Container>
    );
  }

  if (!treeData) {
    return (
      <Container size="xl" py="md">
        <Paper p="md">
          <Text>No data available</Text>
        </Paper>
      </Container>
    );
  }

  // If we're in editor mode, show the QueryEditor component
  if (currentView === 'editor' && editingQueryKey) {
    return (
      <Container size="xl" py="md">
        <Stack gap="md">
          <Paper p="md" withBorder>
            <Group justify="space-between">
              <Group gap="xs">
                <IconDatabase size={20} />
                <Title order={2}>Query Editor</Title>
                <Badge variant="light">{editingQueryKey}</Badge>
              </Group>
              <Button
                variant="light"
                onClick={handleBackToExplorer}
                leftSection={<IconChevronLeft size={16} />}
              >
                Back to Explorer
              </Button>
            </Group>
          </Paper>

          <QueryEditor queryKey={editingQueryKey} />
        </Stack>
      </Container>
    );
  }

  // Show the explorer view
  return (
    <>
      <Container size="xl" py="md">
        <Stack gap="md">
          <Paper p="md" withBorder>
            <Group gap="xs">
              <IconDatabase size={24} />
              <Title order={1}>Query Commander</Title>
              <Badge color="blue" variant="light">Explorer</Badge>
              {openWindows.size > 0 && (
                <Badge color="green" variant="light">
                  {openWindows.size} editor{openWindows.size !== 1 ? 's' : ''} open
                </Badge>
              )}
            </Group>
            <Text size="sm" mt="xs">
              Browse the feature tree and manage your queries. Click on terminal nodes to edit queries.
            </Text>
          </Paper>

          <Grid>
            <Grid.Col span={5}>
              <Paper p="md" withBorder h="75vh">
                <Stack gap="md">
                  <Group gap="xs">
                    <IconFolder size={20} />
                    <Title order={4}>Feature Tree</Title>
                    <Badge size="sm"  >
                      {treeData.length} root {treeData.length === 1 ? 'node' : 'nodes'}
                    </Badge>
                  </Group>
                  <Divider />
                  <ScrollArea style={{ height: 'calc(75vh - 120px)' }}>
                    <Stack gap={2}>
                      {treeData.map((node) => (
                        <TreeNode
                          key={node.id}
                          node={node}
                          onNodeSelect={handleNodeSelect}
                          selectedNodeId={selectedNode?.id}
                          expandedNodes={expandedNodes}
                          setExpandedNodes={setExpandedNodes}
                        />
                      ))}
                    </Stack>
                  </ScrollArea>
                </Stack>
              </Paper>
            </Grid.Col>

            <Grid.Col span={7}>
              <Paper p="md" withBorder h="75vh">
                <Stack gap="md">
                  <Group gap="xs">
                    <IconInfoCircle size={20} />
                    <Title order={4}>
                      {selectedNode ?
                        (selectedNode.nodeType === 'Query' ? 'Query Details' :
                          selectedNode.nodeType === 'Feature' ? 'Feature Details' : 'Node Details') :
                        'Details'
                      }
                    </Title>
                    {selectedNode && (
                      <Badge color={getNodeColor(selectedNode.nodeType, selectedNode.queryType)}>
                        {selectedNode.nodeType}
                      </Badge>
                    )}
                  </Group>
                  <Divider />
                  <ScrollArea style={{ height: 'calc(75vh - 120px)' }}>
                    <QueryDetails
                      node={selectedNode}
                      onEditQuery={handleEditQuery}
                      onEditQueryNewWindow={handleEditQueryNewWindow}
                      onEditFeature={handleEditFeature}
                    />
                  </ScrollArea>
                </Stack>
              </Paper>
            </Grid.Col>
          </Grid>
        </Stack>
      </Container>

      {/* Render query editor windows */}
      {Array.from(openWindows).map(queryKey => (
        <QueryEditorWindow
          key={queryKey}
          queryKey={queryKey}
          onClose={() => handleCloseQueryWindow(queryKey)}
        />
      ))}
    </>
  );
};

export default NewQueryCommanderHome;